/**
 * Client-Seite des X84/X85-Preis-Riegels (Gegenstück zu shared/gaeb-entry-prices.ts und
 * server/routes/gaeb-export.ts::rejectMissingPriceExport): der Server verweigert die
 * Angebotsabgabe mit unbepreisten Positionen per HTTP 422 + `code:
 * 'GAEB_EXPORT_MISSING_PRICES'` (+ missingPriceCount, missingPricePositions). Die bewusste
 * Umgehung (`allowMissingPrices: true`) war bisher NUR per direkter API erreichbar — der
 * Export-Dialog zeigte lediglich den Fehler-Toast.
 *
 * Diese reinen Funktionen machen die Dialog-Entscheidung des Export-Clients
 * (client/src/components/gaeb/GAEBExport.tsx) unter node:test prüfbar, ohne .tsx zu laden
 * (Muster: shared/batch-apply-ui-controller.ts):
 *
 *  - {@link leiteGaebPreisLueckenDialogAb}: GENAU 422 + GENAU dieser Code → Daten für den
 *    Bestätigungsdialog; ALLES ANDERE → null (normaler Fehler-Toast). Bewusst KEIN
 *    Vorab-Client-Check — der Server ist die Wahrheit, der Dialog reagiert ausschließlich
 *    auf die 422-Antwort.
 *  - {@link baueGaebExportRequestBody}: der Wiederholungsaufruf nach Bestätigung trägt
 *    `allowMissingPrices: true`; der Erstaufruf trägt den Schlüssel NIE (die Umgehung darf
 *    kein stiller Standard werden — der Server loggt sie als ausdrückliche Entscheidung).
 */

export const GAEB_EXPORT_MISSING_PRICES_CODE = 'GAEB_EXPORT_MISSING_PRICES';

/**
 * Kappung der OZ-Liste — DECKUNGSGLEICH mit dem Server-Fehlertext
 * (shared/gaeb-entry-prices.ts::MAX_LISTED_POSITIONS): lange Listen werden gekappt,
 * aber nie stumm (die Anzahl bleibt vollständig, der Rest wird als "+N weitere" genannt).
 */
const MAX_ANGEZEIGTE_OZ = 20;

export interface GaebPreisLueckenDialogDaten {
  /** Vollständige Anzahl unbepreister Positionen (nie gekappt). */
  anzahl: number;
  /** Dialog-Titel, z.B. "3 Positionen ohne Preis" (Singular: "1 Position ohne Preis"). */
  titel: string;
  /** OZ-Liste, gekappt wie der Server (max. {@link MAX_ANGEZEIGTE_OZ}). */
  angezeigteOz: string[];
  /** Wie viele OZ der Kappung zum Opfer fielen ("+N weitere"); 0 = Liste vollständig. */
  weitereAnzahl: number;
}

/**
 * Entscheidet, ob eine Export-Fehlerantwort den Bestätigungsdialog öffnet.
 * Nur 422 + `code === 'GAEB_EXPORT_MISSING_PRICES'` mit mindestens einer betroffenen
 * Position liefert Dialogdaten — jede andere Antwort (anderer Status, anderer Code,
 * kaputter/leerer Body) liefert null und bleibt beim normalen Fehler-Toast.
 */
export function leiteGaebPreisLueckenDialogAb(
  status: number,
  body: unknown,
): GaebPreisLueckenDialogDaten | null {
  if (status !== 422 || typeof body !== 'object' || body === null) return null;
  const antwort = body as {
    code?: unknown;
    missingPriceCount?: unknown;
    missingPricePositions?: unknown;
  };
  if (antwort.code !== GAEB_EXPORT_MISSING_PRICES_CODE) return null;

  const oz = Array.isArray(antwort.missingPricePositions)
    ? antwort.missingPricePositions.filter((p): p is string => typeof p === 'string')
    : [];
  const anzahl =
    typeof antwort.missingPriceCount === 'number' &&
    Number.isFinite(antwort.missingPriceCount) &&
    antwort.missingPriceCount > 0
      ? antwort.missingPriceCount
      : oz.length;
  // Defensiv: 422 mit Code, aber ohne eine einzige Position — ein leerer Dialog wäre
  // schlechter als der Toast (es gäbe nichts zu bestätigen).
  if (anzahl <= 0) return null;

  const angezeigteOz = oz.slice(0, MAX_ANGEZEIGTE_OZ);
  return {
    anzahl,
    titel: anzahl === 1 ? '1 Position ohne Preis' : `${anzahl} Positionen ohne Preis`,
    angezeigteOz,
    weitereAnzahl: Math.max(0, anzahl - angezeigteOz.length),
  };
}

export interface GaebExportRequestBasis {
  databaseId: string;
  exportFormat: string;
  includeComments: boolean;
}

/**
 * Request-Body für /api/gaeb/export. `allowMissingPrices` erscheint AUSSCHLIESSLICH nach
 * ausdrücklicher Bestätigung im Dialog (dann exakt `true`) — im Normalfall fehlt der
 * Schlüssel vollständig, damit der Server-Riegel greift.
 */
export function baueGaebExportRequestBody(
  basis: GaebExportRequestBasis,
  allowMissingPrices: boolean,
): GaebExportRequestBasis & { allowMissingPrices?: true } {
  if (!allowMissingPrices) return { ...basis };
  return { ...basis, allowMissingPrices: true };
}
